import { useRender } from "@base-ui/react/use-render";
import { cn } from "@simble/shared/lib/cn";
import React from "react";

/**
 * Кросс-фейд «контент ↔ лоадер» на CSS-переходах.
 *
 * Раньше эти два спана были motion.span: ради смены opacity/blur/scale на 0.2 s в бандл ехали
 * motion-dom и framer-motion (~440 KB исходника, ~41 KB gzip). Button рендерится на каждом экране,
 * так что вес лежал в критическом пути любой загрузки. Значения, длительность и кривая сохранены
 * один в один: easeInOut у motion — это cubic-bezier(0.42, 0, 0.58, 1).
 *
 * initial={false} у motion означало «на первом рендере не анимировать» — CSS-переход так себя
 * ведёт сам: на маунте предыдущего значения нет. useReducedMotion заменён на motion-reduce:.
 */
const SWAP_TRANSITION = "transition-[opacity,filter,transform] duration-200 ease-[cubic-bezier(0.42,0,0.58,1)] motion-reduce:transition-none";
const SWAP_HIDDEN = "scale-[0.97] opacity-0 blur-[2px]";
const SWAP_VISIBLE = "scale-100 opacity-100 blur-none";

interface ButtonProps extends React.ComponentProps<"button"> {
  asChild?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  render?: React.ReactElement;
  theme?: "primary" | "secondary" | "text" | "outline";
}

const spinnerClassName = "box-border size-5 shrink-0 animate-spin rounded-full border-2 border-white/32 border-t-white border-solid";
/** На светлой подложке (secondary) белый лоадер не виден — красим его в брендовый цвет. */
const spinnerSecondaryClassName = "border-sber/32 border-t-sber";

const Button = ({ isLoading, disabled, theme = "primary", children, className, leftIcon, asChild = false, render, ...rest }: ButtonProps) => {
  // Для обратной совместимости: если asChild=true, используем первый дочерний элемент как render
  const renderElement: React.ReactElement<Record<string, unknown>> | undefined = React.useMemo(() => {
    if (render) {
      return render as React.ReactElement<Record<string, unknown>>;
    }
    if (asChild) {
      const firstChild = React.Children.toArray(children)[0];
      if (React.isValidElement(firstChild)) {
        return firstChild as React.ReactElement<Record<string, unknown>>;
      }
    }
    return;
  }, [render, asChild, children]);

  const buttonContent = (
    <span className="inline-grid place-items-center *:col-start-1 *:row-start-1">
      <span aria-hidden={isLoading} className={cn("flex items-center justify-center gap-2", SWAP_TRANSITION, isLoading ? SWAP_HIDDEN : SWAP_VISIBLE)}>
        {leftIcon}
        {!asChild && children}
      </span>
      <span
        aria-hidden={!isLoading}
        className={cn("pointer-events-none flex items-center justify-center", SWAP_TRANSITION, isLoading ? SWAP_VISIBLE : SWAP_HIDDEN)}
      >
        <span aria-hidden className={cn(spinnerClassName, theme === "secondary" && spinnerSecondaryClassName)} />
      </span>
    </span>
  );

  const finalRenderElement = React.useMemo((): React.ReactElement<Record<string, unknown>> | undefined => {
    if (!renderElement) {
      return;
    }

    if (isLoading || leftIcon) {
      return React.cloneElement(renderElement, {
        children: (
          <>
            {!!isLoading && (
              <span aria-hidden className={cn(spinnerClassName, "size-6 border", theme === "secondary" && spinnerSecondaryClassName)} />
            )}
            {leftIcon}
            {renderElement.props.children}
          </>
        ),
      });
    }

    return renderElement;
  }, [renderElement, isLoading, leftIcon, theme]);

  const renderParams: Parameters<typeof useRender>[0] = {
    defaultTagName: "button",
    props: {
      "aria-busy": isLoading || undefined,
      className: cn(getThemeClasses(theme), className),
      disabled: disabled || isLoading,
      type: finalRenderElement ? undefined : "button",
      ...rest,
      children: finalRenderElement ? undefined : buttonContent,
    },
  };

  if (finalRenderElement) {
    renderParams.render = finalRenderElement;
  }

  return useRender(renderParams);
};

const getThemeClasses = (theme: ButtonProps["theme"]) => {
  switch (theme) {
    // Вторичная кнопка: серая подложка + брендовый текст (макет 2ГИС — «Продолжить» в авторизации)
    case "secondary":
      return "cursor-pointer rounded-xl bg-button-secondary px-4 py-4 text-accent flex items-center justify-center transition-colors gap-2 disabled:cursor-not-allowed button";
    case "text":
      return "bg-transparent cursor-pointer text-sber transition-colors button disabled:bg-gray-300 transition-colors disabled:cursor-not-allowed whitespace-nowrap py-4 outline-none flex flex-nowrap items-center gap-3 justify-start";
    default:
      return "cursor-pointer rounded-xl bg-sber px-4 py-4 text-white flex items-center justify-center transition-colors gap-2 disabled:cursor-not-allowed button";
  }
};

export default Button;
